import { useId, useRef } from 'react';
import { Input } from './input';

export interface OtpInputProps {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  digitLabel: (position: number) => string;
  disabled?: boolean;
  autoFocus?: boolean;
}

/** Six real text fields, with a single controlled code and native OTP autofill. */
export function OtpInput({ value, onValueChange, label, digitLabel, disabled, autoFocus }: OtpInputProps) {
  const id = useId();
  const fields = useRef<(HTMLInputElement | null)[]>([]);
  const focus = (index: number) => { fields.current[index]?.focus(); fields.current[index]?.select(); };
  const update = (index: number, input: string) => {
    const digits = input.replace(/\D/g, '').slice(0, 6);
    if (input && !digits) return;
    const next = value.padEnd(6, ' ').split('');
    const start = digits.length === 6 ? 0 : index;
    if (!digits) next[index] = ' ';
    else [...digits].forEach((digit, offset) => { if (start + offset < 6) next[start + offset] = digit; });
    onValueChange(next.join('').trimEnd());
    if (digits) focus(Math.min(start + digits.length, 5));
  };
  return <div role="group" aria-labelledby={id} className="flex flex-col gap-2">
    <span id={id} className="text-sm font-medium">{label}</span>
    <div className="grid grid-cols-6 gap-2">
      {Array.from({ length: 6 }, (_, index) => <Input
        key={index} ref={element => { fields.current[index] = element; }}
        aria-label={digitLabel(index + 1)} inputMode="numeric" pattern="[0-9]*"
        autoComplete={index === 0 ? 'one-time-code' : 'off'} autoFocus={autoFocus && index === 0}
        disabled={disabled} value={value[index]?.trim() ?? ''}
        className="min-h-12 px-0 text-center text-lg font-medium tabular-nums"
        onFocus={event => event.target.select()}
        onChange={event => update(index, event.target.value)}
        onPaste={event => { event.preventDefault(); update(index, event.clipboardData.getData('text')); }}
        onKeyDown={event => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault(); focus(Math.max(0, Math.min(5, index + (event.key === 'ArrowLeft' ? -1 : 1))));
          } else if (event.key === 'Backspace') {
            event.preventDefault(); const target = value[index]?.trim() ? index : Math.max(0, index - 1);
            update(target, ''); focus(target);
          } else if (event.key === 'Delete') { event.preventDefault(); update(index, ''); }
        }}
      />)}
    </div>
  </div>;
}
